<template lang="pug">
#receive-message-list(v-cloak)
  .box
    .box-header.pb-0
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .note
            span 入所申込者からの連絡・問い合わせです。
            span 右の設定ボタンから使用／不使用の切り替えができます。
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.setting-btn(type="button" @click="gotoSetting") 設定
    .box-body
      b-tabs(v-model="tabIndex" @activate-tab="activateTab" @changed="changedTab")
        //- family opinion tab
        b-tab.tab(v-if="showMenu('waiterOpinion') && _receiveMessagesFunction != null && _receiveMessagesFunction.showMessages")
          template(v-slot:title)
            .d-flex.flex-row.align-items-center
              .opinion-tab-title
                span.opinion-tab-title-text.text-truncate.d-inline-block 入所申込者からの連絡
                span.badge(v-if="nonFinishedOpinionCount") {{ nonFinishedOpinionCount }}
              span.mr-1(v-if="nonFinishedOpinionCount" :style="getBadgeMargin(nonFinishedOpinionCount)")
              .refresh-icon.ml-1(@click.stop="refreshFamilies" title="再読み込みします")
          OpinionFamilyListTab(ref="opinionFamilyList" :account="account" @refresh="refresh" @onReply="addFamilyReplyTab")

        //- reply tab
        b-tab(v-for="tab in tabs" :key="'tab-' + tab.no")
          template(v-slot:title)
            .message-reply-tab-title
              span.message-reply-tab-title-text.text-truncate.d-inline-block {{ tab.title }}
              button.close(type="button" @click="closeReplylTab") x
          ReplyFamilyOpinionTab(:account="tab.account" :currentOpinion="tab.currentOpinion" :messages="tab.messages" :planType="planType")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { BTab } from "bootstrap-vue";
import { userStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";

import WaiterOpinionsService from "@/commons/services/WaiterOpinions";
import SystemError from "@/commons/models/SystemError";
import { PlanType } from "@/commons/services/Plans";
import AccountsService from "@/commons/services/Accounts";
import { Account, WaiterReceiveMessagesFunction } from "@/commons/services/Authorization";
import { OpinionMessagesGetModel } from "@/commons/services/OpinionMessages";

import OpinionFamilyListTab, { WaiterOpinionMessageTableItem } from "@/pc/containers/waiterReceiveMessages/OpinionFamilyListTab.vue";
import ReplyFamilyOpinionTab from "@/pc/containers/waiterReceiveMessages/ReplyFamilyOpinionTab.vue";

/**
 * 家族からの連絡画面コンポーネント
 */
@Component({
  components: {
    OpinionFamilyListTab,
    ReplyFamilyOpinionTab
  }
})
export default class ReceiveMessageList extends Vue {
  /** 意見サービス */
  @Inject("WaiterOpinionsService") opinionsService!: WaiterOpinionsService;
  /** ユーザーサービス */
  @Inject("AccountsService") accountsService!: AccountsService;

  @Ref()
  opinionFamilyList!: OpinionFamilyListTab;

  /** タブインデックス */
  tabIndex: number = 0;
  /** メッセージ詳細タブ */
  tabs: Array<any> = [];
  /** 詳細タブからの戻り先 */
  backTabIndex: number = 0;
  /** 詳細タブ閉じるボタンが押下されたか */
  onCloseButton: boolean = false;

  /** 未確認の意見数 */
  nonFinishedOpinionCount: number | string = 0;

  /** プラン種別 */
  planType: PlanType = PlanType.UNKNOWN;

  /** アカウント */
  account: Account | null = null;

  get _receiveMessagesFunction(): WaiterReceiveMessagesFunction | null {
    if (this.account) {
      return this.account.permissions.waiterReceiveMessagesFunction;
    } else {
      return null;
    }
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.planType = userStore.facility.planType;
    this.refreshBadge();

    const account: Account = await this.getAccounts();
    this.account = account;

    return;
  }

  /**
   * ### アカウント情報を取得する
   */
  public async getAccounts(): Promise<Account> {
    const account: Account = await this.accountsService.post(userStore);
    return account;
  }

  /**
   * ### バッジを更新する
   */
  public refresh(): void {
    this.refreshBadge();
    this.$emit("refresh");
    return;
  }

  /**
   * ### バッジ数を更新する
   *
   * - バッジの更新でシステムエラーにはしないため、コンソールエラーだけにする
   * - 未確認家族からの連絡
   */
  public async refreshBadge(): Promise<void> {
    Promise.all([
      // 非同期で呼び出している理由は更新が完了次第画面を更新するため
      this.updateNonFinishedOpinionCount()
    ]).then((results: Array<any>) => {
      // 並列処理内の例外はreturnされる(Uncaught (in promise) Error対策)
      // エラーハンドル処理を実行する
      for (const result of results) {
        if (result instanceof SystemError) {
          console.error("error:", result);
        }
      }
    });

    return;
  }

  /**
   * ### 未確認家族からの連絡数を更新する
   */
  public async updateNonFinishedOpinionCount(): Promise<void | SystemError> {
    try {
      const params: any = {
        confirmed: false
      };
      const count: number = await this.opinionsService.getCount(userStore, params);

      // 100件以上の場合は!を格納する
      if (100 <= count) {
        this.nonFinishedOpinionCount = "!";
      } else {
        this.nonFinishedOpinionCount = count;
      }
    } catch (error) {
      // 機能使用不可でステータスコード405が返却された場合はエラーとして扱わない
      const systemError: SystemError = error as SystemError;
      this.nonFinishedOpinionCount = 0;
      if (systemError.statusCode == 405) {
        return undefined;
      } else {
        // Promise.all()で並列に実行されるので例外を投げなおすのではなく例外を戻り値として返す
        return error as SystemError;
      }
    }

    return;
  }

  /**
   * ### メニュー表示可否
   */
  public showMenu(functionName: string): boolean {
    // Objectのプロパティを動的参照したいが、コンパイルエラーになるのでanyにしてから取得する
    const permittedFunctions: any = userStore.facility.permittedFunctions;
    const showMenu: boolean = permittedFunctions[functionName];
    return showMenu;
  }

  /**
   * ### 家族からの連絡タブの画面表示を更新する
   */
  public async refreshFamilies(): Promise<void> {
    await this.opinionFamilyList.refresh();
    return;
  }

  /**
   * ### 未読数バッジはabsoluteで表示しているので、左マージンを計算して返す
   */
  public getBadgeMargin(count: number): string {
    // 10件未満
    if (count < 10) {
      return "margin-left: 24px;";
    }
    // 10件以上
    else if (10 <= count) {
      return "margin-left: 32px;";
    }
    // その他
    else {
      return "";
    }
  }

  /**
   * ### 家族の返信タブを追加する
   *
   * - 現在は詳細タブは1つまで表示可能とする
   * - 返信タブを追加する、表示に必要な情報は子コンポーネントから渡される
   * - 他のタブを選択する、タブの閉じるボタンが押下された場合の戻り先タブを指定する
   */
  public addFamilyReplyTab(opinion: { currentOpinion: WaiterOpinionMessageTableItem; messages: Array<OpinionMessagesGetModel> }): void {
    console.log("addFamilyReplyTab: messages", opinion.messages);
    this.tabs.splice(0, 1);
    this.tabs.push({
      no: 0,
      title: `${opinion.currentOpinion.familyName}様への返信`,
      account: this.account,
      currentOpinion: opinion.currentOpinion,
      messages: opinion.messages
    });
    this.backTabIndex = 0;
    return;
  }

  /**
   * ### メッセージ詳細表示用タブを消す
   */
  public closeReplylTab(): void {
    this.tabs.splice(0, 1);
    this.onCloseButton = true;
    return;
  }

  /**
   * ### タブの切り替えイベント
   *
   * - 返信タブから他のタブへ移動した場合、返信タブを削除する
   */
  public activateTab(newTabIndex: number, prevTabIndex: number): void {
    // タブ位置が返信以外であれば返信タブを削除する
    if (newTabIndex != 1 && prevTabIndex == 1) {
      this.tabs.splice(0, 1);
    }
    return;
  }

  /**
   * ### タブの追加・削除イベント
   *
   * - タブ数が家族からの連絡の1つであれば返信タブにフォーカスする
   * - 返信タブが削除されていた場合、設定されていた戻り先タブ位置をアクティブにする
   * - 返信タブが閉じるボタンで削除されていて、現在タブ数が1つの場合が処理対象
   */
  public changedTab(currentTabs: Array<BTab>): void {
    if (2 <= currentTabs.length && currentTabs[1]) {
      currentTabs[1].activate();
    }
    if (this.onCloseButton == true && 1 == currentTabs.length && currentTabs[this.backTabIndex]) {
      currentTabs[this.backTabIndex].activate();
      this.onCloseButton = false;
    }
    return;
  }

  public gotoSetting(): void {
    this.$router.push({ name: PcUrl.WaiterReceiveMessageSetting });
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#receive-message-list {
  max-width: 1292px;
  margin-left: auto;
  margin-right: auto;

  .box-body {
    .opinion-tab-title {
      position: relative;
      max-height: 23px;
    }

    .other-user-opinion-tab-title {
      position: relative;
      max-height: 23px;
    }

    .badge {
      position: absolute;
      top: 1px;
      z-index: 1001;
      margin: 0 0 0 4px !important;
      min-width: 10px;
      padding: 3px 7px;
      font-size: 14px;
      font-weight: bold;
      color: #fff;
      line-height: 1;
      vertical-align: middle;
      white-space: nowrap;
      text-align: center;
      background-color: $notification-badge-color;
      border-radius: 10px;
    }

    .spinner {
      width: 16px;
      height: 16px;
      color: $loading;
    }
    .refresh-icon {
      @include svg-icon("../../../assets/refresh.svg", $font-base-color, 20px, 20px);
      opacity: 0.5;
      text-shadow: 0 1px 0 #fff;
      cursor: pointer;
    }
    .refresh-icon:hover {
      opacity: 0.75;
    }

    .message-reply-tab-title {
      max-height: 23px;

      .message-reply-tab-title-text {
        max-width: 220px;
        margin-right: 12px;
      }
    }
  }
}
</style>
